import { cn } from '@/lib/utils';

/**
 * The app's one status vocabulary (rendered by components/Status.tsx). Every coloured state — a pill, a row edge,
 * a notice, a line of status text — resolves to one of these five, and each
 * means one thing everywhere:
 *
 *   danger   late or wrong: past its date and not done, failed, blocked,
 *            a delivery problem, priced below cost, an error.
 *   warning  needs you: due within the week, a gap (no date, not set up,
 *            unsized), a decision or a count to settle, something flagged.
 *   info     moving, someone else has it: in transit, in progress, waiting
 *            on the vendor, requested and being watched, a nudge queued.
 *   success  done or ready: ready, live, delivered, credited, balanced.
 *   neutral  a plain fact or a kind: a stage, a category, a date with no
 *            urgency.
 *
 * Kinds are never coloured by hue — an action's kind, a discrepancy's
 * direction, a journey's lifecycle status are neutral — so a colour always
 * reads as a state. The calendar's kind colours are a separate palette
 * (lib/theme/buildSeries.ts) and never carry state.
 */
export type StatusTone = 'danger' | 'warning' | 'info' | 'success' | 'neutral';

/** Worst first: the order to pick a combined state by. */
export const STATUS_RANK: Record<StatusTone, number> = { danger: 0, warning: 1, info: 2, success: 3, neutral: 4 };

export function worstTone(tones: Array<StatusTone | null | undefined>): StatusTone | null {
  const present = tones.filter((t): t is StatusTone => t != null);
  return present.length ? present.sort((a, b) => STATUS_RANK[a] - STATUS_RANK[b])[0] : null;
}

// Written out in full: Tailwind only emits classes it finds as literal text.
const PILL_TONE: Record<StatusTone, string> = {
  danger: 'bg-danger-surface text-danger-ink',
  warning: 'bg-warning-surface text-warning-ink',
  info: 'bg-info-surface text-info-ink',
  success: 'bg-success-surface text-success-ink',
  neutral: 'bg-neutral-surface text-neutral-ink',
};

const INK_TONE: Record<StatusTone, string> = {
  danger: 'text-danger-ink',
  warning: 'text-warning-ink',
  info: 'text-info-ink',
  success: 'text-success-ink',
  neutral: 'text-muted-foreground',
};

/** The pill's classes, for an element that must be something else (a link, a button). */
export function statusPillClass(tone: StatusTone = 'neutral'): string {
  return cn(
    'inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-xs leading-4 font-medium whitespace-nowrap tabular-nums [&>svg]:size-3 [&>svg]:shrink-0',
    PILL_TONE[tone],
  );
}

/** Status-coloured text, for a state inside a sentence or a table column. */
export function statusInk(tone: StatusTone = 'neutral'): string {
  return INK_TONE[tone];
}
